import { useCallback, useEffect, useState } from 'react';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

// The north-star's latest measured value for one strategy, as the daily worker last wrote it.
export interface StrategyLatestProgress {
	strategy_id: string;
	metric_id?: string;
	current_value: number;
	trajectory?: string | null;
	basis?: string | null;
	target_value?: number | null;
	next_checkpoint?: number | null;
	projected_arrival?: string | null;
	measured_at?: string;
}

export interface UseLatestProgress {
	byStrategyId: Record<string, StrategyLatestProgress>;
	loading: boolean;
	refresh: () => Promise<void>;
}

// One request covers every card. The dock renders the owner's whole strategy list, so asking
// the metrics rollup per strategy would be one request per card; the worker already wrote each
// north-star's current value into its reading, and the route returns all of them at once.
//
// Refetches on `revision` for the same reason usePendingProposals does: a worker tick that
// measures progress also touches the strategy doc, so the counter that tells the dock its list
// is stale tells this hook the same thing.
export function useLatestProgress(
	opts: { enabled?: boolean; revision?: number } = {},
): UseLatestProgress {
	const { enabled = true, revision } = opts;
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [byStrategyId, setByStrategyId] = useState<
		Record<string, StrategyLatestProgress>
	>({});
	const [loading, setLoading] = useState(enabled);

	const refresh = useCallback(async () => {
		if (!userId || !enabled) return;
		try {
			const rows = await bridgewayApi.get<StrategyLatestProgress[]>(
				`/api/user/${userId}/strategy-reviews/latest-progress`,
			);
			const next: Record<string, StrategyLatestProgress> = {};
			for (const row of Array.isArray(rows) ? rows : []) {
				if (row?.strategy_id) next[row.strategy_id] = row;
			}
			setByStrategyId(next);
		} catch (err) {
			// The gauge falls back to its goal-only rendering, which is honest: a ring with no
			// measured arc says "this is the target" rather than claiming progress we failed to
			// read.
			logger.error('Failed to load latest strategy progress', err);
		} finally {
			setLoading(false);
		}
	}, [userId, enabled, bridgewayApi]);

	useEffect(() => {
		void refresh();
	}, [refresh, revision]);

	return { byStrategyId, loading, refresh };
}
